import React, { useState, useEffect } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Paper, 
  Alert, 
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  IconButton
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { api } from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`auth-tabpanel-${index}`}
      aria-labelledby={`auth-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `auth-tab-${index}`,
    'aria-controls': `auth-tabpanel-${index}`,
  };
}

// Connection options for different tenants
const getConnectionOptions = (tenantId?: string) => {
  switch (tenantId) {
    case 'evanston':
      return [
        'Resident of Evanston',
        'Resident of surrounding communities',
        'Regular visitor to Evanston',
        'Business owner in Evanston',
        'Employee in Evanston',
        'Student at Northwestern University',
        'Faculty/Staff at Northwestern University',
        'Other'
      ];
    case 'severance':
    default:
      return [
        'Resident of Cleveland Heights',
        'Resident of University Heights',
        'Regular visitor to Severance',
        'Business owner in the area',
        'Employee in the area',
        'Other'
      ];
  }
};

interface LoginFormProps {
  onClose?: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onClose }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [connection, setConnection] = useState('');
  const [otherConnection, setOtherConnection] = useState('');
  const [tabValue, setTabValue] = useState(1); // Default to signup (index 1)
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showProfileCompletion, setShowProfileCompletion] = useState(false);
  const [isFirstTimeProfileUpdate, setIsFirstTimeProfileUpdate] = useState(false);
  
  const { login, signup, loginWithGoogle, isAuthenticated, currentUser, userProfile, logout, updateUserProfile } = useAuth();
  const { tenantConfig } = useTenant();
  
  // Get tenant-specific connection options
  const connectionOptions = getConnectionOptions(tenantConfig?.tenant_id);

  // Populate fields if user is authenticated and check if profile completion is needed
  useEffect(() => {
    if (isAuthenticated && userProfile) {
      setFullName(userProfile.displayName || '');
      setConnection(userProfile.connection || '');
      
      // Show profile completion if user is missing connection info (common for Google signups)
      const needsProfileCompletion = !userProfile.connection || userProfile.connection.trim() === '';
      setShowProfileCompletion(needsProfileCompletion);
      setIsFirstTimeProfileUpdate(needsProfileCompletion);
    }
  }, [isAuthenticated, userProfile]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError(null);
    setSuccess(null);
  };

  const handleConnectionChange = (event: SelectChangeEvent<string>) => {
    setConnection(event.target.value);
  };

  const validateForm = (isSignUp: boolean) => {
    if (!email) {
      setError('Email is required');
      return false;
    }
    
    if (!password) {
      setError('Password is required');
      return false;
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }

    if (isSignUp) {
      if (!fullName) {
        setError('Full name is required');
        return false;
      }
      
      if (!connection) {
        const projectName = tenantConfig?.tenant_id === 'evanston' ? 'Evanston PAW' : 'Severance Town Center';
        setError(`Please select your connection to ${projectName}`);
        return false;
      }
      
      if (connection === 'Other' && !otherConnection) {
        const projectName = tenantConfig?.tenant_id === 'evanston' ? 'Evanston PAW' : 'Severance Town Center';
        setError(`Please specify your connection to ${projectName}`);
        return false;
      }
    }
    
    return true;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm(false)) return;
    
    try {
      setIsSubmitting(true);
      setError(null);
      
      const user = await login(email, password);
      const token = await user.getIdToken();
      api.setAuthToken(token);
      
      setSuccess('Login successful!');
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || 'Failed to login. Please check your credentials.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm(true)) return;
    
    try {
      setIsSubmitting(true);
      setError(null);
      
      // Create profile object
      const connectionValue = connection === 'Other' ? otherConnection : connection;
      const profile = {
        displayName: fullName,
        connection: connectionValue
      };
      
      // Call signup with profile info
      const user = await signup(email, password, profile);
      const token = await user.getIdToken();
      api.setAuthToken(token);
      
      setSuccess('Account created successfully!');
    } catch (err: any) {
      console.error('Signup error:', err);
      
      // Check for specific errors about existing accounts
      if (err.message?.includes("account with this email already exists")) {
        setError(err.message + " Would you like to log in instead?");
        // Optionally switch to login tab
        setTabValue(0);
      } else {
        setError(err.message || 'Failed to create account. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setIsSubmitting(true);
      setError(null);
      
      await loginWithGoogle();
      setSuccess('Login successful!');
    } catch (err: any) {
      console.error('Google Sign-in error:', err);
      setError(err.message || 'Failed to login with Google. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Update profile function
  const handleProfileUpdate = async () => {
    if (!isAuthenticated || !currentUser) {
      setError('You must be logged in to update your profile');
      return;
    }
    
    if (!fullName) {
      setError('Full name is required');
      return;
    }
    
    if (!connection) {
      const projectName = tenantConfig?.tenant_id === 'evanston' ? 'Evanston PAW' : 'Severance Town Center';
      setError(`Please select your connection to ${projectName}`);
      return;
    }
    
    if (connection === 'Other' && !otherConnection) {
      const projectName = tenantConfig?.tenant_id === 'evanston' ? 'Evanston PAW' : 'Severance Town Center';
      setError(`Please specify your connection to ${projectName}`);
      return;
    }
    
    try {
      setIsSubmitting(true);
      setError(null);
      
      const connectionValue = connection === 'Other' ? otherConnection : connection;
      
      // Update profile using the context function
      await updateUserProfile({
        displayName: fullName,
        connection: connectionValue
      });
      
      setSuccess('Profile updated successfully!');
      setShowProfileCompletion(false); // Hide profile completion form after successful update
      
      // Close the modal if this was a first-time profile completion
      if (isFirstTimeProfileUpdate && onClose) {
        setTimeout(() => {
          onClose();
        }, 2000); // Give user time to see the success message
      }
      
      // Reset the first-time flag after update
      setIsFirstTimeProfileUpdate(false);
    } catch (err: any) {
      console.error('Profile update error:', err);
      setError(err.message || 'Failed to update profile. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      api.setAuthToken(null);
      setSuccess('Logged out successfully!');
    } catch (err: any) {
      console.error('Logout error:', err);
      setError(err.message || 'Failed to logout. Please try again.');
    }
  };

  return (
    <Paper sx={{ p: 0, width: '100%', maxWidth: 400, mx: 'auto', position: 'relative' }}>
      {/* Close button in top right corner */}
      {onClose && (
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            backgroundColor: 'background.paper',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
      
      {isAuthenticated ? (
        <Box sx={{ p: 3, pt: onClose ? 5 : 3 }}>
          <Typography variant="h6" gutterBottom>
            Logged In
          </Typography>
          
          <Alert severity="info" sx={{ mb: 2 }}>
            Logged in as {userProfile?.displayName || currentUser?.email}
            {userProfile?.connection && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Connection: {userProfile.connection}
              </Typography>
            )}
          </Alert>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}
          
          {showProfileCompletion && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="subtitle1" gutterBottom>
                Complete Your Profile
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Help us understand your connection to the {tenantConfig?.tenant_id === 'evanston' ? 'Evanston PAW' : 'Severance Town Center'} project.
              </Typography>
              
              <TextField
                label="Full Name"
                fullWidth
                margin="normal"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                disabled={isSubmitting}
              />
              
              <FormControl fullWidth margin="normal">
                <InputLabel id="connection-label-update">
                  Connection to {tenantConfig?.tenant_id === 'evanston' ? 'Evanston PAW' : 'Severance Town Center'}
                </InputLabel>
                <Select
                  labelId="connection-label-update"
                  value={connection}
                  label="Connection to Severance"
                  onChange={handleConnectionChange}
                  disabled={isSubmitting}
                >
                  {connectionOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {connection === 'Other' && (
                <TextField
                  label="Please specify"
                  fullWidth
                  margin="normal"
                  value={otherConnection}
                  onChange={(e) => setOtherConnection(e.target.value)}
                  disabled={isSubmitting}
                />
              )}
              
              <Button
                variant="contained"
                color="primary"
                fullWidth
                disabled={isSubmitting}
                onClick={handleProfileUpdate}
                sx={{ mt: 2 }}
              >
                {isSubmitting ? <CircularProgress size={24} /> : 'Complete Profile'}
              </Button>
            </Box>
          )}
          
          {!showProfileCompletion && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Update Profile
              </Typography>
              
              <TextField
                label="Full Name"
                fullWidth
                margin="normal"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                disabled={isSubmitting}
              />
              
              <FormControl fullWidth margin="normal">
                <InputLabel id="connection-label-update">
                  Connection to {tenantConfig?.tenant_id === 'evanston' ? 'Evanston PAW' : 'Severance Town Center'}
                </InputLabel>
                <Select
                  labelId="connection-label-update"
                  value={connection}
                  label="Connection to Severance"
                  onChange={handleConnectionChange}
                  disabled={isSubmitting}
                >
                  {connectionOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {connection === 'Other' && (
                <TextField
                  label="Please specify"
                  fullWidth
                  margin="normal"
                  value={otherConnection}
                  onChange={(e) => setOtherConnection(e.target.value)}
                  disabled={isSubmitting}
                />
              )}
              
              <Button
                variant="contained"
                color="primary"
                fullWidth
                disabled={isSubmitting}
                onClick={handleProfileUpdate}
                sx={{ mt: 2 }}
              >
                {isSubmitting ? <CircularProgress size={24} /> : 'Update Profile'}
              </Button>
            </Box>
          )}
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            {onClose && (
              <Button 
                variant="contained" 
                color="primary" 
                fullWidth 
                onClick={onClose}
              >
                Continue
              </Button>
            )}
            <Button 
              variant="outlined" 
              color="primary" 
              fullWidth 
              onClick={handleLogout}
            >
              Log Out
            </Button>
          </Box>
        </Box>
      ) : (
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', pt: onClose ? 5 : 0 }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="auth tabs" variant="fullWidth">
              <Tab label="Login" {...a11yProps(0)} />
              <Tab label="Sign Up" {...a11yProps(1)} />
            </Tabs>
          </Box>
          
          <TabPanel value={tabValue} index={0}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}
            
            <Box component="form" onSubmit={handleLogin}>
              <TextField
                label="Email"
                type="email"
                fullWidth
                margin="normal"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
              />
              
              <TextField
                label="Password"
                type="password"
                fullWidth
                margin="normal"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isSubmitting}
              />
              
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={isSubmitting}
                sx={{ mt: 2 }}
              >
                {isSubmitting ? <CircularProgress size={24} /> : 'Login'}
              </Button>
              
              <Divider sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary">OR</Typography>
              </Divider>
              
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<GoogleIcon />}
                onClick={handleGoogleSignIn}
                disabled={isSubmitting}
              >
                Sign in with Google
              </Button>
              
              <Button
                variant="text"
                color="primary"
                fullWidth
                onClick={() => setTabValue(1)}
                sx={{ mt: 1 }}
              >
                Need an account? Sign up
              </Button>
            </Box>
          </TabPanel>
          
          <TabPanel value={tabValue} index={1}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}
            
            <Box component="form" onSubmit={handleSignup}>
              <TextField
                label="Full Name"
                fullWidth
                margin="normal"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                disabled={isSubmitting}
                required
                helperText="Your name will be displayed with your ideas and comments"
              />
              
              <TextField
                label="Email"
                type="email"
                fullWidth
                margin="normal"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
                required
              />
              
              <TextField
                label="Password"
                type="password"
                fullWidth
                margin="normal"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isSubmitting}
                required
                helperText="Password must be at least 6 characters"
              />
              
              <FormControl fullWidth margin="normal" required>
                <InputLabel id="connection-label">
                  Connection to {tenantConfig?.tenant_id === 'evanston' ? 'Evanston PAW' : 'Severance Town Center'}
                </InputLabel>
                <Select
                  labelId="connection-label"
                  value={connection}
                  label={`Connection to ${tenantConfig?.tenant_id === 'evanston' ? 'Evanston PAW' : 'Severance Town Center'}`}
                  onChange={handleConnectionChange}
                  disabled={isSubmitting}
                >
                  {connectionOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {connection === 'Other' && (
                <TextField
                  label="Please specify"
                  fullWidth
                  margin="normal"
                  value={otherConnection}
                  onChange={(e) => setOtherConnection(e.target.value)}
                  disabled={isSubmitting}
                  required
                />
              )}
              
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={isSubmitting}
                sx={{ mt: 2 }}
              >
                {isSubmitting ? <CircularProgress size={24} /> : 'Sign Up'}
              </Button>
              
              <Divider sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary">OR</Typography>
              </Divider>
              
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<GoogleIcon />}
                onClick={handleGoogleSignIn}
                disabled={isSubmitting}
              >
                Sign up with Google
              </Button>
              
              <Button
                variant="text"
                color="primary"
                fullWidth
                onClick={() => setTabValue(0)}
                sx={{ mt: 1 }}
              >
                Already have an account? Login
              </Button>
            </Box>
          </TabPanel>
        </>
      )}
    </Paper>
  );
};

export default LoginForm; 